import React, { Fragment } from "react";
import { useAuthClaims } from "@redriver/cinnamon";
import { withRouter } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { getAgreementsState } from "../../selectors";
import {
  PermissionCheck,
  withCurrentUser,
  withPermissions,
} from "features/../../../shared/components/auth";
import { guidsAreEqual } from "features/../../../shared/modules/helpers";
import {
  Actions,
  Targets,
} from "features/../../../shared/constants/permissions";
import { AppRoutes } from "constants/routes";
import {
  PhysicalDocumentSheetStatePrincipal,
  PhysicalDocumentSheetStateIps,
  SheetActionIps,
  SheetActionPrincipal,
  SheetStateIps,
  SheetStatePrincipal,
} from "features/../../../shared/constants/enums";

import {
  getMasterAgreement,
  getMasterAgreements,
  addExpandedAgreementOrSchedule,
  getAgreementScheduleSheets,
} from "../actions";
import {
  SubmitSheet,
  SetPhysicalDocumentSheetState,
  RenameSheet,
  SheetStateProgressView,
  SendForApproval,
  PreApproveSheetPrincipal,
  PreApproveSheetIps,
  MakeSheetLive,
  UpdateApplicableFrom,
  ResetSheetState,
  AddAdminComment,
  AmendExpiryDate,
  DeclinePreApprovalPrincipal,
  DeclinePreApprovalIps,
  DocumentRecipientsView,
} from "../SheetActions";
import {
  SetSheetOwner,
  CloneSheet,
  DeleteSheet,
  CrystalliseSheet,
  UncrystalliseSheet,
  ArchiveSheet,
  UnarchiveSheet,
  ToggleSheetReadOnly,
  DuplicateToNewMaster,
} from "features/Sheets/ManageSheet/SheetActions";
import { SetCurrentSchedule } from "../SetCurrentSchedule";
import { SetReplacingSchedule } from "../SetReplacingSchedule";
import { VoidEnvelope } from "../SheetActions/VoidEnvelope";
import SendToFunder from "../SheetActions/SendToFunder/SendToFunder";
import { RejectDeal } from "../SheetActions/RejectDeal";
import { useTranslation } from "react-i18next";
import { SetIpsPhysicalApproval } from "../SheetActions/SetIpsPhysicalApproval";
import { SendToSupplier } from "../SheetActions/SendToSupplier";
import { SendToIpsSignatory } from "../SheetActions/SendToIpsSignatory";
import ToggleSheetRewriteOnly from "features/Sheets/ManageSheet/SheetActions/ToggleSheetRewriteOnly";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  Icon,
  Menu,
} from "semantic-ui-react";
import { UserArea } from "mortar/Principal/Enums";
import MoveSheetModal from "../SheetActions/MoveSheet/MoveSheetModal";
import { ImpersonatedUserPermissionCheck } from "modules/ImpersonatedUserPermissions";
import UnlockSheet from "features/Sheets/ManageSheet/SheetActions/UnlockSheet";

const SheetActions = ({
  agreement,
  schedule,
  sheet,
  currentUser,
  hasPermission,
  history,
  onClientSelect,
}) => {
  const { t } = useTranslation("agreements", { keyPrefix: "SheetActions" });
  const dispatch = useDispatch();
  const { filters, companyFilters, pagination } =
    useSelector(getAgreementsState);
  const { userId } = useAuthClaims();

  const dealerId = companyFilters?.dealer?.id;
  let {
      sheetId,
      sheetName,
      ownedBy,
      isReadOnly,
      isRewriteOnly,
      physicalDocumentFlowEnabled,
      availableActionsIps,
      availableActionsPrincipal,
    } = sheet,
    { agreementReference, boilerPlateTandCsId } = agreement,
    { scheduleReference } = schedule;
  const { isSupplierAdminUser, isInternalUser } = currentUser;
  const isAdminUser = isInternalUser || isSupplierAdminUser;
  const isSuperUser = currentUser.userArea === UserArea.IpsSuperUser;

  const canDeleteNonSuperAdmin = process.env.TRADE_AS_IPS
    ? availableActionsIps.canDeleteNonSuperAdmin
    : availableActionsPrincipal.canDeleteNonSuperAdmin;

  const canDeleteAll = hasPermission(Targets.Sheet, Actions.DeleteAll);

  const canDeleteSuperAdmin =
    isSuperUser &&
    canDeleteAll &&
    (process.env.TRADE_AS_IPS
      ? availableActionsIps.canDeleteSuperAdmin
      : availableActionsPrincipal.canDeleteSuperAdmin);

  const canPreApproveAsSupplier =
    hasPermission(Targets.Sheet, Actions.PreApproveSupplier) &&
    (process.env.TRADE_AS_IPS
      ? sheet.stateIps == SheetStateIps.PreLive_AwaitingSupplierPreApproval
      : sheet.statePrincipal ==
          SheetStatePrincipal.PreLive_AwaitingPrincipalPreApproval &&
        !sheet.submittedForPhysicalDocumentFlow);

  const canPreApproveAsIPS =
    process.env.TRADE_AS_IPS &&
    isInternalUser &&
    hasPermission(Targets.Sheet, Actions.PreApproveIPS) &&
    sheet.stateIps == SheetStateIps.PreLive_AwaitingIpsPreApproval;
  const sheetInPreApprovalState = process.env.TRADE_AS_IPS
    ? sheet.stateIps == SheetStateIps.PreLive_AwaitingIpsPreApproval ||
      sheet.stateIps == SheetStateIps.PreLive_AwaitingSupplierPreApproval
    : sheet.statePrincipal ==
      SheetStatePrincipal.PreLive_AwaitingPrincipalPreApproval;

  const canDeleteVoided = hasPermission(Targets.Sheet, Actions.DeleteVoided);

  const nonSuperUserCanDelete =
    canDeleteNonSuperAdmin &&
    ((sheet.isDraft && guidsAreEqual(sheet.ownedById, userId)) ||
      (canDeleteVoided && (sheet.isVoided || sheet.isDraft)));

  const showDeleteSheet =
    canDeleteSuperAdmin ||
    ((!isReadOnly || isAdminUser) &&
      !sheet.isDeclined &&
      nonSuperUserCanDelete);

  const reloadAgreement = () =>
    dispatch(getMasterAgreement(agreement.agreementId, filters));

  const reloadAgreements = () =>
    dispatch(getMasterAgreements({ filters, pagination }, companyFilters));

  const onDuplicateToNewMasterSubmitted = (response) => {
    reloadAgreements();
    dispatch(getAgreementScheduleSheets(response.scheduleId, filters));

    // Expand newly created agreement and schedule, so using the response IDs.
    dispatch(addExpandedAgreementOrSchedule(response.masterAgreementId));
    dispatch(addExpandedAgreementOrSchedule(response.scheduleId));
  };

  const reloadSheetsForSchedule = () =>
    dispatch(getAgreementScheduleSheets(schedule.scheduleId, filters));

  //gets the corresponding component the the IPS physical Flow
  const getPhysicalActionForIpsState = (
    state,
    sheet,
    reloadSheetsForSchedule,
  ) => {
    switch (state) {
      case PhysicalDocumentSheetStateIps.Submitted:
        return (
          <SendToSupplier sheet={sheet} onSubmitted={reloadSheetsForSchedule} />
        );
      case PhysicalDocumentSheetStateIps.SentToIpsSignatory:
        return (
          <SetIpsPhysicalApproval
            sheet={sheet}
            onSubmitted={reloadSheetsForSchedule}
          />
        );
      case PhysicalDocumentSheetStateIps.SentToSupplierSignatory:
        return (
          <SendToIpsSignatory
            sheet={sheet}
            onSubmitted={reloadSheetsForSchedule}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Dropdown
      icon
      trigger={<Icon name="ellipsis horizontal" />}
      vertical
      pointing="right"
    >
      <DropdownMenu>
        {!sheet.isReadOnly && (
          <Dropdown
            item
            icon
            pointing="right"
            trigger={
              <div className="flex">
                <Icon name="caret left" />
                {t("Groups.New_Sheet_Actions")}
              </div>
            }
          >
            <DropdownMenu>
              {!sheet.isDeclined && (
                <CloneSheet
                  sheetId={sheetId}
                  name={sheetName}
                  isLive={sheet.isLive}
                />
              )}
              {!sheet.isDeclined && (
                <ImpersonatedUserPermissionCheck
                  action={Actions.DuplicateToNewMaster}
                  target={Targets.Sheet}
                >
                  <DuplicateToNewMaster
                    sheetId={sheetId}
                    name={sheetName}
                    onSubmitted={onDuplicateToNewMasterSubmitted}
                  />
                </ImpersonatedUserPermissionCheck>
              )}
            </DropdownMenu>
          </Dropdown>
        )}
        {!sheet.isReadOnly && (
          <Dropdown
            item
            icon
            pointing="right"
            trigger={
              <div className="flex">
                <Icon name="caret left" />
                {t("Groups.Sheet_Actions")}
              </div>
            }
          >
            <DropdownMenu>
              {(!isReadOnly || isAdminUser) && !sheet.isDeclined && (
                <RenameSheet
                  sheet={sheet}
                  onSubmitted={reloadSheetsForSchedule}
                />
              )}
              {showDeleteSheet && (
                <DeleteSheet
                  sheet={sheet}
                  onSubmitted={() => {
                    reloadSheetsForSchedule();
                    reloadAgreement();
                  }}
                  submitParams={{ sheetId }}
                />
              )}
              {isAdminUser && !sheet.isDeclined && (
                <AmendExpiryDate
                  sheet={sheet}
                  onSubmitted={reloadSheetsForSchedule}
                />
              )}
              {isAdminUser && !sheet.isDeclined && (
                <ImpersonatedUserPermissionCheck
                  target={Targets.Sheet}
                  action={Actions.UpdateSheetScheduleReference}
                >
                  <SetCurrentSchedule
                    sheet={sheet}
                    onSubmitted={reloadSheetsForSchedule}
                  />
                </ImpersonatedUserPermissionCheck>
              )}
              {isInternalUser && !sheet.isDeclined && (
                <SetReplacingSchedule
                  sheet={sheet}
                  onSubmitted={reloadSheetsForSchedule}
                />
              )}
            </DropdownMenu>
          </Dropdown>
        )}
        <Dropdown
          item
          icon
          pointing="right"
          trigger={
            <div className="flex">
              <Icon name="caret left" />
              {t("Groups.Document_Actions")}
            </div>
          }
        >
          <DropdownMenu>
            {!sheetInPreApprovalState &&
              !sheet.isDeclined &&
              !sheet.submittedForPhysicalDocumentFlow && (
                <ImpersonatedUserPermissionCheck
                  target={Targets.Sheet}
                  action={Actions.SendForDocuSignApproval}
                >
                  <SendForApproval
                    sheet={sheet}
                    onSubmitted={() => {
                      reloadSheetsForSchedule();
                      reloadAgreement();
                    }}
                    scheduleReference={scheduleReference}
                    agreementReference={agreementReference}
                    boilerPlateTandCsId={boilerPlateTandCsId}
                    sheetActionRequired={
                      process.env.TRADE_AS_IPS
                        ? SheetActionIps.CanSendForApproval
                        : SheetActionPrincipal.CanSendForApproval
                    }
                  />
                </ImpersonatedUserPermissionCheck>
              )}
            {!sheet.submittedForPhysicalDocumentFlow &&
              !sheet.submittedForDocusignFlow &&
              (!isReadOnly || isAdminUser) &&
              physicalDocumentFlowEnabled && (
                <ImpersonatedUserPermissionCheck
                  target={Targets.Sheet}
                  action={Actions.Submit}
                >
                  <SubmitSheet
                    sheet={sheet}
                    onSubmitted={reloadSheetsForSchedule}
                    boilerPlateTandCsId={boilerPlateTandCsId}
                  />
                </ImpersonatedUserPermissionCheck>
              )}
            {sheet.submittedForDocusignFlow && (
              <ImpersonatedUserPermissionCheck
                target={Targets.Sheet}
                action={Actions.ViewDocumentRecipients}
              >
                <DocumentRecipientsView sheetId={sheetId} />
              </ImpersonatedUserPermissionCheck>
            )}

            {process.env.TRADE_AS_IPS &&
              !sheet.isDeclined &&
              (canPreApproveAsSupplier || canPreApproveAsIPS) && (
                <PreApproveSheetIps
                  sheet={sheet}
                  onSubmitted={reloadSheetsForSchedule}
                />
              )}
            {!process.env.TRADE_AS_IPS &&
              canPreApproveAsSupplier &&
              !sheet.isDeclined && (
                <PreApproveSheetPrincipal
                  sheet={sheet}
                  onSubmitted={() => {
                    reloadSheetsForSchedule();
                    reloadAgreement();
                  }}
                  scheduleReference={scheduleReference}
                  agreementReference={agreementReference}
                  boilerPlateTandCsId={boilerPlateTandCsId}
                  sheetActionRequired={
                    SheetActionPrincipal.CanBePreApprovedByPrincipal
                  }
                />
              )}

            {process.env.TRADE_AS_IPS &&
              !sheet.isDeclined &&
              (canPreApproveAsSupplier || canPreApproveAsIPS) && (
                <DeclinePreApprovalIps
                  sheet={sheet}
                  onSubmitted={reloadSheetsForSchedule}
                />
              )}

            {!process.env.TRADE_AS_IPS &&
              !sheet.isDeclined &&
              canPreApproveAsSupplier && (
                <DeclinePreApprovalPrincipal
                  sheet={sheet}
                  onSubmitted={reloadSheetsForSchedule}
                />
              )}
            {sheet.isInDocuSign && (
              <ImpersonatedUserPermissionCheck
                action={Actions.Void}
                target={Targets.Sheet}
              >
                <VoidEnvelope
                  sheet={sheet}
                  onSubmitted={() => {
                    reloadSheetsForSchedule();
                    reloadAgreement();
                  }}
                />
              </ImpersonatedUserPermissionCheck>
            )}
            {isAdminUser && (
              <ImpersonatedUserPermissionCheck
                action={Actions.Reset}
                target={Targets.Sheet}
              >
                <ResetSheetState
                  sheet={sheet}
                  onSubmitted={reloadSheetsForSchedule}
                  sheetActionRequired={
                    process.env.TRADE_AS_IPS
                      ? SheetActionIps.CanResetSheetState
                      : SheetActionPrincipal.CanResetSheetState
                  }
                />
              </ImpersonatedUserPermissionCheck>
            )}
            {!sheet.submittedForPhysicalDocumentFlow && !sheet.isDeclined && (
              <SheetStateProgressView sheet={sheet} />
            )}
            {!sheet.isDeclined && (
              <React.Fragment>
                {!process.env.TRADE_AS_IPS &&
                  isAdminUser &&
                  sheet.submittedForPhysicalDocumentFlow &&
                  !sheet.submittedForDocusignFlow &&
                  sheet.physicalDocumentSheetStatePrincipal ===
                    PhysicalDocumentSheetStatePrincipal.SubmittedToPrincipalAdmin && (
                    <ImpersonatedUserPermissionCheck
                      target={Targets.Sheet}
                      action={Actions.SetPhysicalDocumentSheetState}
                    >
                      <SendToFunder
                        sheet={sheet}
                        onSubmitted={reloadSheetsForSchedule}
                      />
                    </ImpersonatedUserPermissionCheck>
                  )}
                {process.env.TRADE_AS_IPS &&
                  isAdminUser &&
                  sheet.submittedForPhysicalDocumentFlow &&
                  !sheet.submittedForDocusignFlow &&
                  getPhysicalActionForIpsState(
                    sheet.physicalDocumentSheetStateIps,
                    sheet,
                    reloadSheetsForSchedule,
                  ) && (
                    <ImpersonatedUserPermissionCheck
                      target={Targets.Sheet}
                      action={Actions.SetPhysicalDocumentSheetState}
                    >
                      {getPhysicalActionForIpsState(
                        sheet.physicalDocumentSheetStateIps,
                        sheet,
                        reloadSheetsForSchedule,
                      )}
                    </ImpersonatedUserPermissionCheck>
                  )}
                {isAdminUser && (
                  <ImpersonatedUserPermissionCheck
                    target={Targets.Sheet}
                    action={Actions.RejectDeal}
                  >
                    <RejectDeal
                      sheet={sheet}
                      onSubmitted={reloadSheetsForSchedule}
                    />
                  </ImpersonatedUserPermissionCheck>
                )}
              </React.Fragment>
            )}
          </DropdownMenu>
        </Dropdown>
        <Dropdown
          item
          icon
          pointing="right"
          trigger={
            <div className="flex">
              <Icon name="caret left" />
              {t("Groups.Admin_Actions")}
            </div>
          }
        >
          <DropdownMenu>
            <DropdownItem
              onClick={() =>
                history.push(`${AppRoutes.Notification}?sheetId=${sheetId}`)
              }
            >
              {t("ViewNotifications")}
            </DropdownItem>
            {!isReadOnly && !sheet.isDeclined && (
              <ImpersonatedUserPermissionCheck
                target={Targets.Sheet}
                action={Actions.SetOwner}
              >
                <SetSheetOwner
                  sheetId={sheetId}
                  dealerId={dealerId}
                  ownedBy={ownedBy}
                  onSubmitted={reloadSheetsForSchedule}
                />
              </ImpersonatedUserPermissionCheck>
            )}

            <ImpersonatedUserPermissionCheck target={Targets.Sheet} action={Actions.ClearLock}>
              <UnlockSheet
                sheet={sheet}
                onSubmitted={reloadSheetsForSchedule}
              />
            </ImpersonatedUserPermissionCheck>

            {isInternalUser && !sheet.isLive && (
              <UpdateApplicableFrom
                sheet={sheet}
                scheduleReference={scheduleReference}
                agreementReference={agreementReference}
                onSubmitted={reloadSheetsForSchedule}
                sheetActionRequired={
                  process.env.TRADE_AS_IPS
                    ? SheetActionIps.CanUpdateApplicableFrom
                    : SheetActionPrincipal.CanUpdateApplicableFrom
                }
              />
            )}
            {isAdminUser && !sheet.isDeclined && !isReadOnly && (
              <AddAdminComment
                sheet={sheet}
                onSubmitted={reloadSheetsForSchedule}
              />
            )}
            {!sheet.submittedForDocusignFlow && !isReadOnly && (
              <ImpersonatedUserPermissionCheck
                target={Targets.Sheet}
                action={Actions.SetPhysicalDocumentSheetState}
              >
                <SetPhysicalDocumentSheetState
                  sheet={sheet}
                  onSubmitted={reloadSheetsForSchedule}
                />
              </ImpersonatedUserPermissionCheck>
            )}
            {!isReadOnly && !sheet.isDeclined && (
              <ImpersonatedUserPermissionCheck
                target={Targets.Sheet}
                action={Actions.CrystalliseAndUncrystalliseSheets}
              >
                <UncrystalliseSheet
                  sheet={sheet}
                  onSubmitted={reloadSheetsForSchedule}
                  sheetActionRequired={
                    process.env.TRADE_AS_IPS
                      ? SheetActionIps.CanUncrystallise
                      : SheetActionPrincipal.CanUncrystallise
                  }
                />
                <CrystalliseSheet
                  sheet={sheet}
                  onSubmitted={reloadSheetsForSchedule}
                  sheetActionRequired={
                    process.env.TRADE_AS_IPS
                      ? SheetActionIps.CanCrystallise
                      : SheetActionPrincipal.CanCrystallise
                  }
                />
              </ImpersonatedUserPermissionCheck>
            )}
            {!isReadOnly && !sheet.isDeclined && sheet.isArchived && (
              <UnarchiveSheet
                sheet={sheet}
                onSubmitted={reloadSheetsForSchedule}
              />
            )}
            {!isReadOnly && !sheet.isDeclined && !sheet.isArchived && (
              <ArchiveSheet
                sheet={sheet}
                onSubmitted={reloadSheetsForSchedule}
              />
            )}
            {(!isReadOnly || isAdminUser) && !sheet.isDeclined && (
              <ImpersonatedUserPermissionCheck
                target={Targets.Sheet}
                action={Actions.SetReadOnly}
              >
                <ToggleSheetReadOnly
                  sheet={sheet}
                  onSubmitted={reloadSheetsForSchedule}
                />
              </ImpersonatedUserPermissionCheck>
            )}
            {isAdminUser && !sheet.isDeclined && !isReadOnly && (
              <ImpersonatedUserPermissionCheck
                target={Targets.Sheet}
                action={Actions.SetRewriteOnly}
              >
                <ToggleSheetRewriteOnly
                  sheet={sheet}
                  onSubmitted={reloadSheetsForSchedule}
                />
              </ImpersonatedUserPermissionCheck>
            )}
            {!sheet.isDeclined && !isReadOnly && (
              <MakeSheetLive
                sheet={sheet}
                scheduleIsLive={schedule.isLive}
                sheetActionRequired={
                  process.env.TRADE_AS_IPS
                    ? SheetActionIps.CanMakeLive
                    : SheetActionPrincipal.CanMakeLive
                }
                onSubmitted={() => {
                  reloadSheetsForSchedule();
                  reloadAgreement();
                }}
                scheduleReference={scheduleReference}
                agreementReference={agreementReference}
              />
            )}
            {isAdminUser && (
              <MoveSheetModal
                sheet={sheet}
                reloadSheetsForSchedule={reloadSheetsForSchedule}
                onSubmitted={reloadSheetsForSchedule}
                onClientSelect={onClientSelect}
              />
            )}
          </DropdownMenu>
        </Dropdown>
      </DropdownMenu>
    </Dropdown>
  );
};

export default withPermissions(withCurrentUser(withRouter(SheetActions)));
